import {
  hotelAxisByOffer,
  resolvePolicy,
  type HotelMapLocation,
  type HotelMapView,
  type HotelOfferView,
  type HotelResultsCopy,
  type HotelSearchCopy,
  type HotelSearchValue,
  type HotelStayFieldsCopy,
} from '@voll-intelligence/intelligence-ui-kit';
import {
  type TravelerRef,
  hotelDistanceKm,
  applyHotelPlannerDirectives,
  type HotelPlaceOption,
  HOTEL_MAX_STAY_NIGHTS,
  MAP_AREA_PLACE_ID,
  type ChatUiHotelOffer,
  type HotelPlaceSource,
  type HotelPlannerBlock,
  deckOffersFor,
} from '@voll-intelligence/genui-contract';
import { genUiT } from '../../../shared/ui/i18n.js';
import { hotelOfferView } from '../../shared/hotel_offer_view.js';
import { hotelDateDraftSummary, hotelDateRangeLabel } from './hotel_date_copy.js';

export { hotelDateRangeLabel, hotelSearchDateSummary } from './hotel_date_copy.js';
import type { SelectionInput } from './hotel_selection.js';

type HotelPlannerViewOptions = {
  locale?: string;
  mapboxAccessToken?: string;
  proxyImageUrl?: (url: string) => string;
  detailsAvailable?: boolean;
  defaultMapLocation?: HotelMapLocation;
};

type HotelPlannerView = {
  searchValue: HotelSearchValue;
  searchCopy: HotelSearchCopy;
  resultsCopy: HotelResultsCopy;
  offers: HotelOfferView[];
  map: HotelMapView;
};

type SearchCatalog = {
  places: readonly HotelPlaceOption[];
  travelers: readonly TravelerRef[];
  requesterId?: string;
};

const BELO_HORIZONTE_MAP_LOCATION: HotelMapLocation = {
  coordinates: { latitude: -19.9167, longitude: -43.9345 },
  label: 'Belo Horizonte, MG',
};

function hotelResultsMapCenter(offers: readonly ChatUiHotelOffer[]) {
  const coordinates = offers.filter(
    (offer) =>
      offer.latitude !== undefined &&
      Number.isFinite(offer.latitude) &&
      offer.longitude !== undefined &&
      Number.isFinite(offer.longitude),
  );
  if (coordinates.length === 0) return undefined;
  const total = coordinates.reduce(
    (sum, offer) => {
      const longitudeRadians = ((offer.longitude ?? 0) * Math.PI) / 180;
      return {
        latitude: sum.latitude + (offer.latitude ?? 0),
        longitudeSin: sum.longitudeSin + Math.sin(longitudeRadians),
        longitudeCos: sum.longitudeCos + Math.cos(longitudeRadians),
      };
    },
    { latitude: 0, longitudeSin: 0, longitudeCos: 0 },
  );
  return {
    latitude: total.latitude / coordinates.length,
    longitude: Number(
      ((Math.atan2(total.longitudeSin, total.longitudeCos) * 180) / Math.PI).toFixed(12),
    ),
  };
}

/**
 * Toda a copy da barra de busca, resolvida na locale do turno — o pacote de UI é
 * copy-agnostic e recebe cada frase por prop.
 *
 * Dois pares que parecem redundantes e não são: `destinationLabel`/`datesLabel`/
 * `roomsLabel` são o ASSUNTO de cada campo ("Destino", "Check-in/out",
 * "Quartos") e nomeiam o painel; `whoLabel`/`whereLabel`/`whenLabel` são a
 * PERGUNTA ("Quem", "Onde", "Quando") e são os rótulos visíveis do layout de
 * página. E `datesEmptyLabel` é o vazio da pílula estreita do card ("A
 * definir"), enquanto `datesPlaceholder` é o vazio da página, onde cabe a
 * pergunta inteira.
 */
function searchCopy(locale: string): HotelSearchCopy {
  const t = (key: Parameters<typeof genUiT>[1], vars?: Record<string, string | number>) =>
    genUiT(locale, key, vars);
  return {
    searchLabel: t('hotelPlanner.search'),
    destinationLabel: t('hotelPlanner.destination'),
    destinationPlaceholder: t('hotelPlanner.destinationPlaceholder'),
    myLocationLabel: t('hotelPlanner.myLocation'),
    myLocationErrorLabel: t('hotelPlanner.myLocationError'),
    datesLabel: t('hotelPlanner.checkInOut'),
    roomsLabel: t('hotelPlanner.rooms'),
    /* Os três rótulos VISÍVEIS do layout de página (`naked`), na ordem da
       pergunta do protótipo. São chaves que já existiam para os nomes
       acessíveis da faixa e ninguém lia — o desenho novo as põe na tela. Não
       colidem com `destinationLabel`/`datesLabel`/`roomsLabel`: aqueles são o
       ASSUNTO do campo ("Destino", "Check-in/out", "Quartos"), que segue
       nomeando o popover; estes são a PERGUNTA. */
    whoLabel: t('hotelPlanner.whoLabel'),
    whereLabel: t('hotelPlanner.whereLabel'),
    whenLabel: t('hotelPlanner.whenLabel'),
    policyHintLabel: t('hotelPlanner.policyHint'),
    adjustSearchLabel: t('hotelPlanner.adjustSearch'),
    travelersLabel: t('hotelPlanner.peopleList'),
    noPlacesLabel: t('hotelPlanner.noPlaces'),
    placeErrorLabel: t('hotelPlanner.placeError'),
    noTravelersLabel: t('hotelPlanner.noPeople'),
    travelerErrorLabel: t('travelers.searchError'),
    travelerSearchPlaceholder: t('travelers.searchPlaceholder'),
    loadMoreTravelersLabel: t('travelers.loadMore'),
    calendarLocale: locale,
    previousMonthLabel: t('hotelPlanner.prevMonth'),
    nextMonthLabel: t('hotelPlanner.nextMonth'),
    dateDraftSummary: (value) => hotelDateDraftSummary(value, locale),
    confirmDatesLabel: (value) => {
      const range = hotelDateRangeLabel(value, locale);
      return range ? t('hotelPlanner.confirmDates', { range }) : t('hotelPlanner.confirmDate');
    },
    confirmDatesSheetLabel: t('hotelPlanner.confirmStayDates'),
    confirmAndSearchLabel: t('hotelPlanner.confirmAndSearch'),
    maxStayNights: HOTEL_MAX_STAY_NIGHTS,
    datesEmptyLabel: t('hotelPlanner.datesTBD'),
    datesPlaceholder: t('hotelPlanner.datesPlaceholder'),
    roomLabel: (roomNumber) => t('hotelPlanner.roomN', { n: roomNumber }),
    roomSummary: (roomCount, guestCount) => {
      const roomLabel = t(
        roomCount === 1 ? 'hotelPlanner.roomCount_one' : 'hotelPlanner.roomCount_other',
        { count: roomCount },
      );
      const guestLabel = t(
        guestCount === 1 ? 'hotelPlanner.guestCount_one' : 'hotelPlanner.guestCount_other',
        { count: guestCount },
      );
      /* HÓSPEDES primeiro: a pergunta do campo é "quem", e quantos quartos é a
         consequência dela — a ordem do protótipo em todas as superfícies. */
      return `${guestLabel} · ${roomLabel}`;
    },
    addRoomLabel: t('hotelPlanner.addRoom'),
    maxRoomsLabel: t('hotelPlanner.maxRooms', { n: 5 }),
    maxGuestsLabel: t('hotelPlanner.maxGuests', { n: 9 }),
    chooseGuestLabel: t('hotelPlanner.chooseGuest'),
    companionLabel: t('hotelPlanner.addGuest'),
    addGuestLabel: (roomNumber) => t('hotelPlanner.addGuestAria', { n: roomNumber }),
    removeRoomLabel: (roomNumber) => t('hotelPlanner.removeRoom', { n: roomNumber }),
    backToRoomsLabel: t('hotelPlanner.backToRooms'),
    roomGuestHeading: (roomNumber, companion) =>
      companion
        ? t('hotelPlanner.roomCompanionHeading', { n: roomNumber })
        : t('hotelPlanner.roomGuestHeading', { n: roomNumber }),
    policyTravelerLabel: t('travelers.policyLabel'),
    policyRequesterLabel: t('travelers.you'),
    roomNameNote: t('hotelPlanner.roomNameNote'),
    guestInRoomLabel: (roomNumber) => t('hotelPlanner.guestInRoom', { n: roomNumber }),
    guestCountLabel: (count) =>
      t(count === 1 ? 'hotelPlanner.guestCount_one' : 'hotelPlanner.guestCount_other', { count }),
    holderBadgeLabel: t('hotelPlanner.holderBadge'),
    guestOptionsLabel: (name) => t('hotelPlanner.guestOptions', { name }),
    makeHolderLabel: t('hotelPlanner.makeHolder'),
    swapGuestLabel: t('hotelPlanner.swapGuest'),
    removeFromRoomLabel: t('hotelPlanner.removeFromRoom'),
    guestRoleLabel: t('hotelPlanner.guestRole'),
    doneLabel: t('travelers.done'),
  };
}

/** A copy da fileira da estadia da Página do Hotel — os MESMOS rótulos da
 *  barra de busca mais o aviso de bloqueio das datas ausentes. Derivada de
 *  `searchCopy` de propósito: as duas superfícies mostram os mesmos campos, e
 *  uma copy própria só existiria para divergir. */
export function hotelStayFieldsCopy(locale: string): HotelStayFieldsCopy {
  return {
    ...searchCopy(locale),
    missingDatesAlertLabel: genUiT(locale, 'hotelPlanner.missingDatesAlert'),
    backLabel: genUiT(locale, 'hotelPlanner.stayBack'),
  };
}

function resultsCopy(locale: string): HotelResultsCopy {
  const t = (key: Parameters<typeof genUiT>[1]) => genUiT(locale, key);
  return {
    resultsLabel: t('hotel.resultsListAria'),
    previousResultsLabel: t('hotel.results.previous'),
    nextResultsLabel: t('hotel.results.next'),
    expandLabel: t('hotel.results.seeAll'),
    mapExpandLabel: t('hotel.map.expand'),
    mapZoomInLabel: t('hotel.map.zoomIn'),
    mapZoomOutLabel: t('hotel.map.zoomOut'),
    mapRecenterLabel: t('hotel.map.recenter'),
    retryLabel: t('hotelPlanner.retry'),
    adjustLabel: t('hotelPlanner.adjustSearch'),
    previousPhotoLabel: t('hotel.photoPrev'),
    nextPhotoLabel: t('hotel.photoNext'),
    detailsLabel: t('hotel.map.seeHotel'),
    selectLabel: t('bookingCheckout.select'),
  };
}

function validStay(ui: HotelPlannerBlock): Pick<HotelSearchValue, 'checkin' | 'checkout'> {
  const now = new Date();
  const today = `${String(now.getFullYear())}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const checkin = ui.checkin && ui.checkin >= today ? ui.checkin : undefined;
  const checkout = checkin && ui.checkout && ui.checkout > checkin ? ui.checkout : undefined;
  return {
    ...(checkin ? { checkin } : {}),
    ...(checkout ? { checkout } : {}),
  };
}

/** O lugar do bloco no valor da barra. A marca de "Minha localização" volta
 *  junto: é o que mantém o veto de recente numa nova busca a partir daqui. */
function plannerPlace(
  ui: HotelPlannerBlock,
): Pick<HotelSearchValue, 'place' | 'selectedPlaceId' | 'placeSource'> {
  return {
    place: ui.place ?? ui.placeDetails?.name ?? ui.placeDetails?.city ?? '',
    ...(ui.placeDetails?.placeId ? { selectedPlaceId: ui.placeDetails.placeId } : {}),
    ...(ui.placeSource === 'geolocation' ? { placeSource: ui.placeSource } : {}),
  };
}

function initialSearchValue(ui: HotelPlannerBlock): HotelSearchValue {
  const travelers = ui.travelers ?? [];
  const policyTravelerId = travelers.length
    ? resolvePolicy(travelers, ui.policyTravelerId, ui.requesterId)
    : undefined;
  return {
    ...plannerPlace(ui),
    ...validStay(ui),
    rooms: (ui.rooms ?? [{ guests: 1 }]).map((room) => ({ guests: room.guests })),
    travelerIds: travelers.map((traveler) => traveler.id),
    ...(policyTravelerId ? { policyTravelerId } : {}),
  };
}

/** O mapa do planner: a âncora do destino (ou o centro das ofertas, ou o mapa
 *  contextual sem busca). */
function plannerMapView(
  ui: HotelPlannerBlock,
  offers: readonly ChatUiHotelOffer[],
  options: HotelPlannerViewOptions,
  locale: string,
): HotelMapView {
  const contextualMapLocation = options.defaultMapLocation ?? BELO_HORIZONTE_MAP_LOCATION;
  const resultsMapCenter = hotelResultsMapCenter(offers);
  return {
    available: true,
    ...(options.mapboxAccessToken ? { accessToken: options.mapboxAccessToken } : {}),
    ...(ui.placeDetails
      ? {
          center: {
            latitude: ui.placeDetails.latitude,
            longitude: ui.placeDetails.longitude,
          },
          anchorLabel: ui.placeDetails.name,
        }
      : offers.length > 0
        ? {
            center: resultsMapCenter ?? contextualMapLocation.coordinates,
            anchorLabel: ui.place ?? contextualMapLocation.label,
          }
        : {
            center: contextualMapLocation.coordinates,
            anchorLabel: contextualMapLocation.label,
            contextual: true,
          }),
    // A lista da ÁREA do mapa (#3114): a câmera é a área que a pessoa buscou.
    ...(ui.placeSource === 'map_area' ? { keepFrame: true } : {}),
    fallbackLabel: genUiT(locale, 'hotel.map.aria'),
  };
}

export function createHotelPlannerView(
  ui: HotelPlannerBlock,
  options: HotelPlannerViewOptions = {},
): HotelPlannerView {
  const locale = options.locale ?? 'pt-BR';
  const normalizedOptions = {
    locale,
    proxyImageUrl: options.proxyImageUrl ?? ((url: string) => url),
    detailsAvailable: options.detailsAvailable ?? true,
  };
  // M1 · 1.2 — o agente pode reemitir o bloco VIVO com diretivas (filtro/ordem
  // do painel + destaque). A MESMA função pura vale para o dock e para a
  // projeção de cena do agente (`applyHotelPlannerDirectives`, #2469).
  const offers = applyHotelPlannerDirectives(
    ui.offers ?? [],
    ui.directives,
    ui.nights,
    ui.placeDetails,
  );
  const ordered = deckOffersFor(offers, ui.selectedOfferId, offers.length);
  const axisByOffer = hotelAxisByOffer(offers, (offer) => hotelDistanceKm(offer, ui.placeDetails));
  // #3399 (b): a busca trouxe mais do que o bloco carrega (o card corta em 6,
  // e a diretiva do agente pode cortar mais) — os eixos medidos aqui valem só
  // para o recorte, e o rótulo tem de dizer isso.
  const axisOnCard = ui.offersTotal !== undefined && ui.offersTotal > offers.length;
  const highlighted = new Set(ui.directives?.highlightOfferIds ?? []);
  return {
    searchValue: initialSearchValue(ui),
    searchCopy: searchCopy(locale),
    resultsCopy: resultsCopy(locale),
    offers: ordered.map((offer) =>
      hotelOfferView(offer, {
        ...normalizedOptions,
        ...(ui.nights === undefined ? {} : { nights: ui.nights }),
        ...(ui.placeDetails ? { placeDetails: ui.placeDetails } : {}),
        ...(axisByOffer.has(offer.offerId) ? { axis: axisByOffer.get(offer.offerId) } : {}),
        ...(highlighted.has(offer.offerId) ? { highlighted: true } : {}),
        ...(axisOnCard ? { axisOnCard } : {}),
      }),
    ),
    map: plannerMapView(ui, offers, options, locale),
  };
}

/** A origem do destino do rascunho. A da posição vem no valor; a área do mapa
 *  é o lugar sintético (`MAP_AREA_PLACE_ID`), reconhecível pelo próprio id. */
function draftPlaceSource(value: HotelSearchValue): HotelPlaceSource | undefined {
  if (value.placeSource) return value.placeSource;
  return value.selectedPlaceId === MAP_AREA_PLACE_ID ? 'map_area' : undefined;
}

export function resolveHotelSearchDraft(
  value: HotelSearchValue,
  catalog: SearchCatalog,
): Omit<
  SelectionInput,
  'destinationId' | 'maxPrice' | 'onlyBreakfast' | 'onlyRefundable' | 'onlyPolicyWithIn'
> {
  const place = catalog.places.find((option) => option.placeId === value.selectedPlaceId);
  const travelers = value.travelerIds.flatMap((id) => {
    const traveler = catalog.travelers.find((candidate) => candidate.id === id);
    return traveler ? [{ id: traveler.id, name: traveler.name }] : [];
  });
  const policyTravelerId = travelers.length
    ? resolvePolicy(travelers, value.policyTravelerId, catalog.requesterId)
    : undefined;
  return {
    place: value.place,
    placeRef: place?.ref,
    placeDetails: place?.searchPlace,
    placeSource: draftPlaceSource(value),
    checkin: value.checkin,
    checkout: value.checkout,
    rooms: value.rooms.map((room) => ({ guests: room.guests })),
    travelers,
    policyTravelerId,
  };
}
